import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Laptop, Smartphone, WashingMachine, Gamepad2, Battery, Wrench } from 'lucide-react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const defaultCarouselItems = [
  {
    id: 1,
    title: 'Ремонт iPhone',
    description: 'Заміна АКБ та дисплея. Оригінальні запчастини, гарантія до 90 днів',
    link: '/remont-iphone',
    buttonText: 'Дізнатися ціну',
    image: 'https://cimm.od.ua/images/generated/iphone-hero.png'
  },
  {
    id: 2,
    title: 'Чистка MacBook',
    description: 'Air та Pro: чистка й заміна термопасти, усунення throttling',
    link: '/chistka-macbook',
    buttonText: 'Детальніше',
    image: 'https://cimm.od.ua/images/generated/macbook-hero.png'
  },
  {
    id: 3,
    title: 'Ремонт телефонів',
    description: 'Швидкий ремонт iPhone, Samsung, Xiaomi',
    link: '/phones',
    buttonText: 'Детальніше',
    image: 'https://images.unsplash.com/photo-1556656793-08538906a9f8?w=1200&q=80'
  },
  {
    id: 4,
    title: 'Ремонт ноутбуків',
    description: 'Професійний ремонт будь-якої складності',
    link: '/laptops',
    buttonText: 'Детальніше',
    image: 'https://images.unsplash.com/photo-1588872657578-7efd1f1555ed?w=1200&q=80'
  },
  {
    id: 5,
    title: 'Чистка ноутбуків',
    description: 'Чистка від пилу та заміна термопасти. Звичайні та ігрові',
    link: '/chistka-noutbukiv',
    buttonText: 'Детальніше',
    image: 'https://cimm.od.ua/images/generated/laptop-hero.png'
  },
  {
    id: 6,
    title: "Чистка комп'ютерів",
    description: 'Стаціонарні ПК: продування, термопаста, тиша і прохолода',
    link: '/chistka-pk',
    buttonText: 'Детальніше',
    image: 'https://cimm.od.ua/images/generated/pc-hero.png'
  },
  {
    id: 7,
    title: 'Чистка консолей',
    description: 'PS5, PS4, Xbox — заміна термопасти, зниження температур до −20°C',
    link: '/chistka-konsoley',
    buttonText: 'Детальніше',
    image: 'https://cimm.od.ua/images/generated/console-hero.png'
  }
];

const HeroCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [carouselItems, setCarouselItems] = useState(defaultCarouselItems);

  // Load carousel from API
  useEffect(() => {
    const loadCarousel = async () => {
      try {
        const response = await fetch(`${API_URL}/api/carousel`);
        if (response.ok) {
          const data = await response.json();
          if (data && data.length > 0) {
            setCarouselItems(data);
          }
        }
      } catch (error) {
        console.error('Error loading carousel:', error);
        // Use default items on error
      }
    };
    loadCarousel();
  }, []);

  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % carouselItems.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, carouselItems.length]);

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + carouselItems.length) % carouselItems.length);
    setIsAutoPlaying(false);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % carouselItems.length);
    setIsAutoPlaying(false);
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
  };

  const currentItem = carouselItems[currentIndex];

  return (
    <div className="hero-carousel relative w-full h-[400px] md:h-[500px] lg:h-[600px] overflow-hidden rounded-2xl border border-orange-500/20">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <img 
          src={currentItem.image} 
          alt={currentItem.title}
          className="w-full h-full object-cover transition-all duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/40"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 h-full flex items-center px-4 md:px-8 lg:px-16">
        <div className="max-w-2xl space-y-4 md:space-y-6 pb-16 md:pb-20">
          
          <h2 className="text-3xl md:text-4xl lg:text-6xl font-bold text-white leading-tight" style={{textShadow: '2px 2px 4px rgba(0,0,0,0.8)'}}>
            {currentItem.title}
          </h2>
          
          <p className="text-base md:text-lg lg:text-xl text-gray-200" style={{textShadow: '2px 2px 4px rgba(0,0,0,0.8)'}}>
            {currentItem.description}
          </p>

          <Link 
            to={currentItem.link}
            className="inline-flex items-center gap-2 px-6 py-3 md:px-8 md:py-4 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg transition-all duration-300 hover:shadow-[0_0_30px_rgba(255,140,0,0.5)] text-sm md:text-base"
          >
            {currentItem.buttonText || 'Дізнатися більше'}
            <ChevronRight className="w-4 h-4 md:w-5 md:h-5" />
          </Link>
        </div>
      </div>

      {/* Navigation Arrows - hidden on mobile, visible on tablet+ */}
      <button
        onClick={goToPrevious}
        className="hidden md:flex absolute left-2 md:left-4 top-1/2 -translate-y-1/2 z-20 p-2 md:p-3 bg-black/50 hover:bg-orange-500/80 backdrop-blur-sm border border-orange-500/30 rounded-full transition-all duration-300"
        aria-label="Previous slide"
      >
        <ChevronLeft className="w-5 h-5 md:w-6 md:h-6 text-white" />
      </button>

      <button
        onClick={goToNext}
        className="hidden md:flex absolute right-2 md:right-4 top-1/2 -translate-y-1/2 z-20 p-2 md:p-3 bg-black/50 hover:bg-orange-500/80 backdrop-blur-sm border border-orange-500/30 rounded-full transition-all duration-300"
        aria-label="Next slide"
      >
        <ChevronRight className="w-5 h-5 md:w-6 md:h-6 text-white" />
      </button>

      {/* Dots Indicator */}
      <div className="absolute bottom-4 md:bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-2">
        {carouselItems.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-2 h-2 md:w-3 md:h-3 rounded-full transition-all duration-300 ${
              index === currentIndex
                ? 'bg-orange-500 w-6 md:w-8'
                : 'bg-white/50 hover:bg-white/80'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default HeroCarousel;
